<script setup>
import { AxiosError } from 'axios';
import { reactive, computed, watch, onMounted } from 'vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CustomDataProviderValue from './CustomDataProviderValue.vue';
import CustomDataProviderAPI from 'dashboard/api/integrations/customDataProvider';

const props = defineProps({
  // Either a conversation (conversation sidebar) or a contact (contact detail
  // page) provides the customer the data is fetched for.
  conversationId: {
    type: [Number, String],
    default: null,
  },
  contactId: {
    type: [Number, String],
    default: null,
  },
  hook: {
    type: Object,
    default: () => ({}),
  },
  // When true (contact detail page) the iframe stretches to fill the height of
  // its container instead of using a fixed minimum height.
  fullHeight: {
    type: Boolean,
    default: false,
  },
});

const state = reactive({
  isLoading: true,
  isError: false,
  contentType: null, // 'html' | 'json'
  data: null,
});

const api = new CustomDataProviderAPI(props.hook.id);

// We display exactly what the provider returns – values are not converted.
// Hash/Array values are expanded recursively into key/value rows (any depth).
const isComplex = value => value !== null && typeof value === 'object';

const entries = computed(() => {
  if (
    state.contentType !== 'json' ||
    !state.data ||
    typeof state.data !== 'object'
  )
    return [];
  return Object.entries(state.data).map(([key, value]) => ({
    key,
    value,
    isComplex: isComplex(value),
  }));
});

const isHtml = computed(() => state.contentType === 'html' && !!state.data);

const fetchData = async () => {
  state.isLoading = true;
  state.isError = false;
  state.contentType = null;
  state.data = null;
  try {
    const response = await api.fetchData({
      conversationId: props.conversationId,
      contactId: props.contactId,
    });
    if (response.data?.error) {
      // eslint-disable-next-line no-console
      console.error('CustomDataProvider Error:', response.data.error);
      state.isError = true;
      return;
    }
    state.contentType = response.data.content_type;
    state.data = response.data.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      // eslint-disable-next-line no-console
      console.error(
        'CustomDataProvider Response:',
        error.response?.status,
        error.response?.data
      );
      state.isError = true;
      return;
    }
    throw error;
  } finally {
    state.isLoading = false;
  }
};

onMounted(fetchData);

watch(
  () => [props.conversationId, props.contactId],
  () => fetchData()
);
</script>

<template>
  <div
    class="custom-data-provider"
    :class="{ 'flex flex-col h-full': fullHeight }"
  >
    <div
      v-if="state.isLoading"
      class="flex justify-center py-4 text-n-slate-11"
    >
      <Spinner :size="20" />
    </div>
    <div v-else-if="state.isError" class="py-2 text-center text-n-slate-11">
      {{ $t('CONVERSATION_CUSTOM_DATA_PROVIDER.LOAD_ERROR') }}
    </div>
    <iframe
      v-else-if="isHtml"
      :srcdoc="state.data"
      class="w-full border-0 rounded-md bg-white"
      :class="fullHeight ? 'flex-1 min-h-0' : 'min-h-[200px]'"
      sandbox="allow-scripts allow-popups allow-forms"
      :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CUSTOM_DATA_PROVIDER')"
    />
    <div v-else-if="entries.length">
      <div
        v-for="entry in entries"
        :key="entry.key"
        class="overflow-auto py-3 px-4"
      >
        <div class="items-center flex justify-between mb-1.5">
          <span class="text-sm font-medium text-slate-800 dark:text-slate-100">
            {{ entry.key }}
          </span>
        </div>
        <CustomDataProviderValue
          v-if="entry.isComplex"
          :data="entry.value"
          class="pl-3"
        />
        <div v-else class="break-words">{{ entry.value }}</div>
      </div>
    </div>
    <div v-else class="py-2 text-center text-n-slate-11">
      {{ $t('CONVERSATION_CUSTOM_DATA_PROVIDER.NO_RECORDS_FOUND') }}
    </div>
  </div>
</template>
